/** @file Parses a skinned mesh's flat `skin` array: count, then (bone, px, py, weight) per influence. */
import type { MeshSkinData } from '../../skeletonData';
import { coreFail } from '../coreFail';
import { indexIn } from './indexIn';

/** Skinned MeshSkinData for vertexCount vertices; VALIDATION at path when the array is short, long or bad. */
export function readMeshSkin(skin: readonly number[], vertexCount: number, boneCount: number, path: string): MeshSkinData {
  const counts = new Int32Array(vertexCount);
  let cursor = 0;
  let total = 0;
  for (let v = 0; v < vertexCount; v++) {
    const count = skin[cursor] ?? -1;
    if (!Number.isInteger(count) || count < 1) coreFail('VALIDATION', `vertex ${v} needs a positive influence count`, { path });
    counts[v] = count;
    total += count;
    cursor += 1 + 4 * count;
  }
  if (cursor !== skin.length) coreFail('VALIDATION', `skin holds ${skin.length} numbers; ${cursor} expected for ${vertexCount} vertices`, { path });
  const bones = new Int32Array(total);
  const positions = new Float64Array(2 * total);
  const weights = new Float32Array(total);
  cursor = 0;
  let k = 0;
  for (let v = 0; v < vertexCount; v++) {
    const count = counts[v] ?? 0;
    cursor += 1;
    for (let j = 0; j < count; j++) {
      bones[k] = indexIn(skin[cursor] ?? -1, boneCount, path);
      positions[2 * k] = skin[cursor + 1] ?? 0;
      positions[2 * k + 1] = skin[cursor + 2] ?? 0;
      weights[k] = skin[cursor + 3] ?? 0;
      cursor += 4;
      k += 1;
    }
  }
  return { kind: 'skinned', counts, bones, positions, weights };
}
